import React, { useState } from "react";
import { EyeIcon, EyeOffIcon, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "../../../../shared/hooks/use-toast";
import { Button } from "../../../../shared/ui/button";
import { DynamicForm } from "../../../../shared/components/DynamicForm";
import { useAuth } from "../../hooks";
import { logEvent } from "@shared/analytics/analytics";
import ImageSection from "./ImageSection";

const Login = () => {
  const { toast } = useToast();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loginFields: FormField[] = [
    {
      name: "email",
      type: "email",
      label: "Email Address",
      required: true,
      placeholder: "Enter your email",
      defaultValue: "",
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      fullWidth: true,
      classes: {
        helperText: "text-xs text-gray-500 mt-1",
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-gray-50",
        error: "text-red-600 text-sm mt-1",
      },
    },
    {
      name: "password",
      type: "password",
      label: "Password",
      required: true,
      placeholder: "Enter your password",
      minLength: 6,
      defaultValue: "",
      fullWidth: true,
      classes: {
        helperText: "text-xs text-gray-500 mt-1",
        container: "w-full mb-4 relative",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input:
          "w-full h-10 px-3 pr-10 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-gray-50",
        error: "text-red-600 text-sm mt-1",
      },
    },
  ];
  const form = FormService.createForm({
    fields: loginFields,
    mode: "onChange",
    validateOnMount: false,
  });
  const handleSubmit = async (data: { email: string; password: string }) => {
    try {
      setLoading(true);
      setError(null);
      await login(data.email, data.password);
      toast({
        title: "Login Successful",
        description: "Welcome back!",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      toast({
        title: "Login Error",
        description: errorMessage,
        variant: "destructive",
      });
      logEvent("Login Failed", {
        email: data.email,
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };
  const handleReset = () => {
    form.reset();
    setRememberMe(false);
  };
  const handleFieldChange = (fieldName: string) => {
    if (FormService.getFieldError(form, fieldName)) {
      form.clearErrors(fieldName);
    }
  };
  const renderCustomFooter = () => (
    <div className="space-y-4 mt-6">
      <div className="flex items-center justify-between">
        <label className="flex items-center space-x-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 text-blue-600 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
          />
          <span>Remember me</span>
        </label>
        <Button
          type="button"
          variant="link"
          className="p-0 h-auto text-blue-600 hover:text-blue-800 hover:underline text-sm"
          onClick={() => {
            logEvent("Password Reset Clicked", { source: "login-form" });
            navigate("/forgot-password");
          }}
          disabled={loading}
        >
          Forgot password?
        </Button>
      </div>
      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg transition-colors font-medium text-base"
        disabled={loading}
      >
        {loading ? "Signing in..." : "Log In"}
      </Button>
    </div>
  );
  return (
    <div className="flex h-screen">
      {/* Left Section */}

      {/* <div className="w-1/2  flex">
        <img
          src="Smart360.png"
          alt="Login Background"
          className="w-full h-full"
        />
      </div> */}
      <ImageSection/>

      {/* Right Section - Form */}
      <div className="w-1/2 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-sm flex flex-col gap-5">
          <div>
            <h2 className="page-title">Welcome Back</h2>
            <p className="page-subtitle ">
              Log in to access your SMART360 dashboard.
            </p>
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <DynamicForm
            fields={loginFields.map((f) =>
              f.name === "password"
                ? { ...f, type: showPassword ? "text" : "password" }
                : f
            )}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={loading}
            config={{
              showProgress: false,
              layout: "vertical",
              spacing: "none",
              submitButtonText: "Log In",
              showResetButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-0",
                fieldContainer: "",
                buttonContainer: "hidden",
                fieldGroup: "",
              },
            }}
            renderCustomFooter={renderCustomFooter}
            style={{
              background: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          />
          {/* <div className="mt-6 text-center text-sm text-gray-600">
            Don’t have an account?{" "}
            <Button
              type="button"
              variant="link"
              className="p-0 h-auto text-blue-600 hover:underline"
              onClick={() => toast({ title: "Contact Admin", description: "Please reach out to your admin." })}
            >
              Contact Admin
            </Button>
          </div> */}
        </div>
      </div>
    </div>
  );
};
export default Login;
